/**
 * How a loan tape is set up (DOP-481 Settings, restyled DOP-627).
 *
 * There is no config resource to PATCH: the configuration IS the last run's
 * question. So changing the files is not "save", it is "run with these
 * instead". A screen that let you save a config the tape was not built from
 * would be showing two answers to "what is this workflow".
 */

import { Loader2 } from "lucide-react";
import React, { useState } from "react";
import type {
	SchemaExtractorDocRef,
	SchemaExtractorPayload,
} from "../SchemaExtractorForm";
import LoanTapeSetupForm from "./LoanTapeSetupForm";
import MonitoringSettingsCard from "../Monitoring/MonitoringSettingsCard";

export interface TapeSetupProps {
	config: SchemaExtractorPayload | null;
	editing: boolean;
	/** Disabled while a run is in flight: a change of files starts a run. */
	canEdit: boolean;
	/**
	 * Disabled while a run is in flight. Deleting the chat takes its stream
	 * generations with it, so a delete mid-run would destroy the extraction
	 * out from under itself.
	 */
	canDelete: boolean;
	onEdit: () => void;
	onCancel: () => void;
	onSubmit: (payload: SchemaExtractorPayload) => void;
	/** Removes the workflow, which is to say the chat it is made of. */
	onDelete: () => void;
	deleting: boolean;
}

const fileName = (ref: SchemaExtractorDocRef): string =>
	ref.filename ?? ref.deal_name ?? ref.doc_id;

const FILE_ROW =
	"flex items-center gap-2.5 rounded-lg bg-[#fafafa] px-3 py-2.5 text-[13.5px] text-[#1F2937]";

const SECONDARY =
	"inline-flex h-9 items-center whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-medium text-[#1F2937] hover:bg-gray-50 disabled:opacity-60";

const TapeSetup: React.FC<TapeSetupProps> = ({
	config,
	editing,
	canEdit,
	canDelete,
	onEdit,
	onCancel,
	onSubmit,
	onDelete,
	deleting,
}) => {
	// Confirmation in place rather than a dialog: the whole affordance is two
	// buttons, and a modal to guard two buttons is more than the decision needs.
	const [confirming, setConfirming] = useState(false);

	if (editing) {
		return (
			<LoanTapeSetupForm
				mode="edit"
				initial={config}
				saving={false}
				onSubmit={(payload) => onSubmit(payload)}
				onCancel={onCancel}
			/>
		);
	}

	const deleteCard = (
		<section className="rounded-xl border border-gray-200 bg-white p-5">
			<h3 className="text-[15px] font-semibold text-[#111111]">
				Delete this loan tape
			</h3>
			<p className="mt-1 text-[13px] text-[#5B6472]">
				Its files, every run and the Excel files they produced go with it. This
				cannot be undone.
			</p>
			{!canDelete && (
				<p className="mt-3 text-[13px] text-[#5B6472]">
					Wait for the run to finish.
				</p>
			)}
			{confirming ? (
				<div className="mt-3 flex items-center gap-2">
					<button
						type="button"
						onClick={onDelete}
						disabled={deleting || !canDelete}
						className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#b42318] px-3.5 text-sm font-semibold text-white hover:bg-[#912018] disabled:opacity-60"
					>
						{deleting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
						Delete permanently
					</button>
					<button
						type="button"
						onClick={() => setConfirming(false)}
						disabled={deleting}
						className={SECONDARY}
					>
						Keep it
					</button>
				</div>
			) : (
				<button
					type="button"
					onClick={() => setConfirming(true)}
					disabled={!canDelete}
					className="mt-3 text-[13px] font-medium text-[#b42318] underline underline-offset-[3px] disabled:opacity-60"
				>
					Delete this loan tape
				</button>
			)}
		</section>
	);

	if (!config) {
		return (
			<div className="grid max-w-3xl gap-4">
				<p className="rounded-xl border border-gray-200 bg-white px-4 py-6 text-center text-sm text-[#5B6472]">
					This loan tape has no stored files: its runs were sent from chat.
				</p>
				{deleteCard}
			</div>
		);
	}

	return (
		<div className="grid items-start gap-4 xl:grid-cols-2">
			<div className="grid gap-4">
				<MonitoringSettingsCard title="The example Doppler copies">
					<p className="-mt-1 mb-2.5 text-[12.5px] text-[#6B7280]">
						Your format comes from this pair: a raw file and the tape you made
						from it.
					</p>
					<div className={FILE_ROW}>
						<span className="min-w-0 flex-1 break-words">
							{fileName(config.training_source)}{" "}
							<span className="text-[#9a9ea3]" aria-label="becomes">
								→
							</span>{" "}
							{fileName(config.training_target)}
						</span>
					</div>
				</MonitoringSettingsCard>

				<MonitoringSettingsCard title="The file it cleans">
					<p className="-mt-1 mb-2.5 text-[12.5px] text-[#6B7280]">
						Doppler reads this raw file the way it learned from the example.
					</p>
					<div className={FILE_ROW}>
						<span className="min-w-0 flex-1 break-words">
							{fileName(config.new_source)}
						</span>
					</div>
				</MonitoringSettingsCard>

				<div>
					<button
						type="button"
						onClick={onEdit}
						disabled={!canEdit}
						className={SECONDARY}
					>
						Change the files
					</button>
				</div>
			</div>

			<div className="grid gap-4">
				<MonitoringSettingsCard title="The file you download">
					<div className={FILE_ROW}>
						<span className="min-w-0 flex-1 break-words">
							{config.output_filename?.trim() || "Named after the file it cleans"}
						</span>
					</div>
				</MonitoringSettingsCard>
				{deleteCard}
			</div>
		</div>
	);
};

export default TapeSetup;
